import React, { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import { exportResourceLibraryReport } from "../../api/exportResourceLibraryReport/exportResourceLibraryReportApi";
import { exportFileManagerReport } from "../../api/exportFileManagerReport/exportFileManagerReportApi";

interface ReportDefinition {
  key: string;
  name: string;
  description: string;
  filenameBase: string;
  fetch: () => Promise<Blob>;
}

const REPORTS: ReportDefinition[] = [
  {
    key: "resource-library",
    name: "HUB Resource Report",
    description:
      "Every resource (published and unpublished) with type, publish status, " +
      "Process Wheel Slice, Stage, trades, categories, tags, and job titles.",
    filenameBase: "hub-resource-report",
    fetch: exportResourceLibraryReport,
  },
  {
    key: "file-manager",
    name: "File Manager Report",
    description:
      "Every file in the CertainPath file manager with its folder path, " +
      "where it's used (events, resources), and duplicate detection.",
    filenameBase: "file-manager-report",
    fetch: exportFileManagerReport,
  },
];

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ReportsPage: React.FC = () => {
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = (report: ReportDefinition) => {
    setDownloading(report.key);
    const date = new Date().toISOString().slice(0, 10);
    report
      .fetch()
      .then((blob) => {
        downloadBlob(blob, `${report.filenameBase}-${date}.csv`);
      })
      .finally(() => setDownloading(null));
  };

  return (
    <MainPageWrapper
      subtitle="Exports run against live data — download whenever you need a current copy."
      title="Reports"
    >
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg divide-y divide-gray-100 dark:divide-slate-700/50">
        {REPORTS.map((report) => (
          <div
            className="flex items-center justify-between gap-4 px-4 py-4"
            key={report.key}
          >
            <div className="min-w-0">
              <p className="text-sm text-gray-900 dark:text-white">
                {report.name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {report.description}
              </p>
            </div>
            <Button
              disabled={downloading !== null}
              onClick={() => handleDownload(report)}
              variant="outline"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              {downloading === report.key ? "Preparing…" : "Download CSV"}
            </Button>
          </div>
        ))}
      </div>
    </MainPageWrapper>
  );
};

export default ReportsPage;
